<template>
  <div class="bd-pie-chart-wrapper">
    <div v-if="!hasData" class="text-muted small">No data available.</div>
    <div v-else ref="chartEl" class="bd-pie-chart"></div>
  </div>
</template>

<script>
import { onMounted, onBeforeUnmount, ref, watch, computed } from 'vue'

const GOOGLE_LOADER_SRC = 'https://www.gstatic.com/charts/loader.js'

function loadGoogleCharts() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      return reject(new Error('Window is undefined'))
    }

    if (window.google && window.google.charts && window.google.visualization) {
      window.google.charts.load('current', { packages: ['corechart'] })
      window.google.charts.setOnLoadCallback(() => resolve(window.google))
      return
    }

    const existing = document.querySelector(`script[src="${GOOGLE_LOADER_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => {
        window.google.charts.load('current', { packages: ['corechart'] })
        window.google.charts.setOnLoadCallback(() => resolve(window.google))
      })
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Charts')))
      return
    }

    const script = document.createElement('script')
    script.src = GOOGLE_LOADER_SRC
    script.async = true
    script.onload = () => {
      if (!window.google || !window.google.charts) {
        reject(new Error('Google Charts not available'))
        return
      }
      window.google.charts.load('current', { packages: ['corechart'] })
      window.google.charts.setOnLoadCallback(() => resolve(window.google))
    }
    script.onerror = () => reject(new Error('Failed to load Google Charts'))
    document.head.appendChild(script)
  })
}

export default {
  name: 'BDPieChart',
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    categoryKey: {
      type: String,
      required: true,
    },
    valueKey: {
      type: String,
      required: true,
    },
    valueLabel: {
      type: String,
      default: 'Value',
    },
    // Custom colors: array of hex strings for each slice, e.g. ['#0d9488', '#f59e0b', '#ef4444']
    colors: {
      type: Array,
      default: null,
    },
    // Donut hole size: 0 = pie chart, 0.4 = donut chart
    pieHole: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const chartEl = ref(null)
    const chart = ref(null)
    const googleRef = ref(null)

    const hasData = computed(() => Array.isArray(props.data) && props.data.length > 0)

    const buildDataTable = () => {
      if (!googleRef.value || !hasData.value) return null
      const google = googleRef.value
      const dataTable = new google.visualization.DataTable()
      dataTable.addColumn('string', 'Category')
      dataTable.addColumn('number', props.valueLabel || 'Value')

      const rows = props.data.map((row) => {
        const category = row[props.categoryKey] || 'Unknown'
        const value = Number(row[props.valueKey] ?? 0)
        return [String(category), isNaN(value) ? 0 : value]
      })
      dataTable.addRows(rows)
      return dataTable
    }

    const drawChart = async () => {
      if (!chartEl.value || !hasData.value) return

      try {
        if (!googleRef.value) {
          googleRef.value = await loadGoogleCharts()
        }
        const google = googleRef.value
        const dataTable = buildDataTable()
        if (!dataTable) return

        if (!chart.value) {
          chart.value = new google.visualization.PieChart(chartEl.value)
        }

        const options = {
          // Labeled legend draws labels outside slices with pointers
          legend: { position: 'labeled', textStyle: { fontSize: 11 } },
          pieSliceText: 'none',
          // Slightly reduce pie radius and increase outer margins so
          // the connector lines appear shorter and a bit inclined.
          chartArea: { left: '18%', top: '10%', width: '64%', height: '80%' },
          sliceVisibilityThreshold: 0,
          pieHole: props.pieHole,
          pieStartAngle: 20,
          height: 260,
        }
        if (Array.isArray(props.colors) && props.colors.length > 0) {
          options.colors = props.colors
        }

        chart.value.draw(dataTable, options)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to render BD pie chart', e)
      }
    }

    onMounted(() => {
      drawChart()
    })

    watch(
      () => [props.data, props.valueKey, props.categoryKey, props.colors],
      () => {
        drawChart()
      },
      { deep: true },
    )

    onBeforeUnmount(() => {
      chart.value = null
    })

    return {
      chartEl,
      hasData,
    }
  },
}
</script>

<style scoped>
.bd-pie-chart-wrapper {
  width: 100%;
  min-height: 260px;
}

.bd-pie-chart {
  width: 100%;
  height: 260px;
}
</style>
